if (!customElements.get("tp-product-features")) {
  class TpProductFeatures extends HTMLElement {
    static TRACKING_STORAGE_KEY = "tp-product-features-tracking";

    // true = navigate to the next product, false = swap sections in place
    static USE_FULL_PAGE_LOAD = false;

    static publishProductFeatureEvent(eventName, options = {}) {
      const eventdata = {
        timestamp: new Date().toISOString(),
        name: options.name != null ? options.name : eventName,
        data: {
          data: options.data != null ? options.data : {},
          type: options.type,
          productid: options.productId,
          variantId: options.variantId,
        },
      };
      Object.keys(eventdata.data).forEach((key) => {
        if (eventdata.data[key] === undefined) delete eventdata.data[key];
      });

      try {
        const raw = localStorage.getItem(
          TpProductFeatures.TRACKING_STORAGE_KEY,
        );
        const list = raw ? JSON.parse(raw) : [];
        list.push({ eventName, ...eventdata });
        localStorage.setItem(
          TpProductFeatures.TRACKING_STORAGE_KEY,
          JSON.stringify(list),
        );
      } catch (e) {
        console.warn(
          "tp-product-features: could not write tracking to localStorage",
          e,
        );
      }

      if (
        typeof Shopify === "undefined" ||
        !Shopify.analytics ||
        typeof Shopify.analytics.publish !== "function"
      )
        return;
      Shopify.analytics.publish(eventName, eventdata);
    }

    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;
      console.log("tp-product-features initialized...");

      this.sectionsToReplace =
        this.dataset.sectionsToReplace || "v2-main-product";
      this.bindFeatureInputs();
      this.initGallerySwipers();
      this.initDeliveryMessages();
      this.initProductForms();
      this.initAccessoryInputClickListeners();
      this.initPopstateHandler();
    }

    getSectionRoot() {
      const sectionId = this.dataset.sectionId;
      if (!sectionId) return this;

      return document.querySelector(`#${sectionId}`) || this;
    }

    initRecommendationsInReplacedSections(sectionsIds) {
      if (typeof window.initProductRecommendations !== "function") return;
      (sectionsIds || []).forEach((sectionId) => {
        const container = document.querySelector(
          `#shopify-section-${sectionId}`,
        );
        if (!container) return;
        container
          .querySelectorAll(".product-recommendations[data-url]")
          .forEach((section) => {
            window.initProductRecommendations(section);
          });
      });
    }

    disconnectedCallback() {
      this.destroyGallerySwipers();
      this.removeResizeHandler();
      this.removePopstateHandler();
      this._initialized = false;
    }

    bindFeatureInputs(root = this) {
      const featureInputs = root.querySelectorAll(".tp-product-feature-input");

      featureInputs.forEach((input) => {
        if (input.dataset.featureListenerAttached === "true") return;
        input.dataset.featureListenerAttached = "true";
        input.addEventListener("change", () => this.handleFeatureChange(input));
      });
    }

    async handleFeatureChange(input) {
      const prodHandle = input.checked
        ? input.dataset.addProdHandle
        : input.dataset.removeProdHandle;

      if (!prodHandle) return;

      const featureNameEl = input
        .closest(".tp-product-feature")
        ?.querySelector(".tp-product-feature-name");
      const featureName = featureNameEl ? featureNameEl.textContent.trim() : "";
      const currentUrl = window.location.href;
      const currentUrlObj = new URL(currentUrl);
      const currentPathParts = currentUrlObj.pathname
        .split("/")
        .filter((part) => part);
      const currentProductHandle =
        currentPathParts.length >= 2 && currentPathParts[0] === "products"
          ? currentPathParts[1]
          : null;

      TpProductFeatures.publishProductFeatureEvent("product_feature_selected", {
        name: "product_feature_interaction",
        type: "product_feature_selected",
        data: {
          featureName: featureName || undefined,
          productHandle: prodHandle,
          currentProductHandle: currentProductHandle || undefined,
          added: input.checked,
        },
      });

      // Full page load so analytics platforms record the product view
      if (TpProductFeatures.USE_FULL_PAGE_LOAD) {
        const nextUrlElement = document.querySelector(
          `#product-features-urls #${prodHandle}`,
        );
        const nextUrl = nextUrlElement ? nextUrlElement.textContent.trim() : "";

        window.location.href =
          nextUrl || window.Shopify.routes.root + `products/${prodHandle}`;
        return;
      }

      try {
        const sections = this.sectionsToReplace.split(",");

        const sectionsIds = sections.map((section) => {
          const sectionElement = document.querySelector(`.${section}`);
          return sectionElement
            ? sectionElement.id.replace("shopify-section-", "")
            : null;
        });

        const sectionsIdsString = sectionsIds.join(",");

        const response = await fetch(
          window.Shopify.routes.root +
            `products/${prodHandle}?sections=${sectionsIdsString}`,
        );
        const sectionJson = await response.json();

        sectionsIds.forEach((sectionId) => {
          const currentContainer = document.querySelector(
            `#shopify-section-${sectionId}`,
          );
          if (!currentContainer) return;

          const newHtml = sectionJson[sectionId];
          currentContainer.innerHTML = newHtml;
        });

        this.initRecommendationsInReplacedSections(sectionsIds);

        if (window.innerWidth > 1023.98) {
          window.scrollTo({
            top: 0,
            behavior: "smooth",
          });
        }

        this.destroyGallerySwipers();
        this.removeResizeHandler();
        window.yotpo.initWidgets();

        // Change URL - use pushState to add to history instead of replaceState
        const nextUrlElement = document.querySelector(
          `#product-features-urls #${prodHandle}`,
        );
        const nextUrl = nextUrlElement ? nextUrlElement.textContent.trim() : "";

        if (nextUrl) {
          // Store state with previous URL and product handle for back navigation
          window.history.pushState(
            {
              previousUrl: currentUrl,
              previousProductHandle: currentProductHandle,
              productHandle: prodHandle,
            },
            "",
            nextUrl,
          );
        }
      } catch (error) {
        console.error("Error:", error);
      }
    }

    initGallerySwipers() {
      const sectionRoot = this.getSectionRoot();
      const thumbEl = sectionRoot.querySelector(".thumb-swiper");
      const mainEl = sectionRoot.querySelector(".main-swiper");
      if (!thumbEl || !mainEl || !window.swiper) return;

      this.thumbSwiper = new window.swiper(thumbEl, {
        spaceBetween: 6,
        slidesPerView: 4,
        watchSlidesProgress: true,
        watchSlidesVisibility: true,
        direction: "horizontal",
        loop: false,
        lazy: true,
        breakpoints: {
          992: {
            slidesPerView: 5,
            spaceBetween: 16,
            direction: "vertical",
          },
        },
        navigation: {
          nextEl: ".swiper-thumb-next",
          prevEl: ".swiper-thumb-prev",
        },
      });

      this.mainSwiper = new window.swiper(mainEl, {
        lazy: true,
        navigation: {
          nextEl: ".swiper-button-next",
          prevEl: ".swiper-button-prev",
        },
        pagination: {
          el: ".main-swiper-pagination",
          clickable: false,
        },
        spaceBetween: 10,
        zoom: false,
        thumbs: {
          swiper: this.thumbSwiper,
        },
        lazy: {
          enabled: true,
          checkInView: false,
          loadOnTransitionStart: true,
          loadPrevNext: true,
          loadPrevNextAmount: 1,
        },
        on: {
          init: () => {
            const sectionRoot = this.getSectionRoot();
            const mainSlide = sectionRoot.querySelector(".main-swiper-slide");
            const thumbSlide = sectionRoot.querySelector(".swiper-slide.thumb");
            const thumbSwiperEl = sectionRoot.querySelector(".thumb-swiper");

            if (!mainSlide || !thumbSlide || !thumbSwiperEl) return;

            mainSlide.style.height = `${mainSlide.offsetWidth - 22}px`;

            if (window.innerWidth > 991.98) {
              thumbSwiperEl.style.height = `${mainSlide.offsetWidth - 44}px`;
            } else {
              thumbSwiperEl.style.height = "82px";
            }

            const galleryContainer =
              sectionRoot.querySelector(".gallery-container");
            if (galleryContainer) galleryContainer.style.opacity = 1;

            this.addResizeHandler();
          },
          slideChange: () => {
            if (this.thumbSwiper) this.thumbSwiper.slideNext(300);
          },
        },
      });
    }

    destroyGallerySwipers() {
      if (this.thumbSwiper?.destroy) this.thumbSwiper.destroy(true, true);
      if (this.mainSwiper?.destroy) this.mainSwiper.destroy(true, true);
      this.thumbSwiper = null;
      this.mainSwiper = null;
    }

    addResizeHandler() {
      if (this._onResize) return;
      this._onResize = () => {
        const sectionRoot = this.getSectionRoot();
        const mainSwiper = sectionRoot.querySelector(".main-swiper");
        const thumbSlide = sectionRoot.querySelector(".swiper-slide.thumb");
        const thumbSwiperEl = sectionRoot.querySelector(".thumb-swiper");

        if (!mainSwiper || !thumbSlide || !thumbSwiperEl) return;

        const mainWidth = mainSwiper.offsetWidth;
        const thumbWidth = thumbSlide.offsetWidth;

        mainSwiper.style.height = `${mainWidth - 40}px`;

        if (window.innerWidth > 991.98) {
          thumbSwiperEl.style.height = `${mainWidth - 40}px`;
        } else {
          thumbSwiperEl.style.height = `${thumbWidth - 40}px`;
        }
      };

      window.addEventListener("resize", this._onResize);
    }

    removeResizeHandler() {
      if (!this._onResize) return;
      window.removeEventListener("resize", this._onResize);
      this._onResize = null;
    }

    initDeliveryMessages() {
      const deliveryMessages = document.querySelectorAll(
        "#main-product .product-information .product-details .delivery-cont",
      );
      if (!deliveryMessages.length) return;

      deliveryMessages.forEach((deliveryMessage) => {
        const titleEl = deliveryMessage.querySelector(".title");
        if (!titleEl) return;

        const isPreorder =
          deliveryMessage.dataset.preorder === "true" ||
          titleEl.hasAttribute("preorder");
        const preorderDateValue =
          deliveryMessage.dataset.preorderDate ||
          titleEl.getAttribute("preorder-date");

        if (isPreorder && preorderDateValue) {
          const preorderDate = new Date(preorderDateValue);
          const dayMessageFormat =
            preorderDate.toLocaleString("en-us", { weekday: "long" }) +
            " " +
            String(preorderDate.getDate()).slice(-2) +
            " " +
            preorderDate.toLocaleString("default", { month: "long" });

          const preorderMsg =
            deliveryMessage.dataset.preorderText ||
            this.dataset.preorderMessage ||
            "";

          titleEl.innerText = preorderMsg
            ? `${preorderMsg} ${dayMessageFormat}`
            : dayMessageFormat;
          return;
        }

        const productId = deliveryMessage.dataset.productId;
        const productPrice = deliveryMessage.dataset.productPrice;
        const productSku = deliveryMessage.dataset.productSku;

        if (!productId || !productPrice || !productSku) {
          deliveryMessage.dataset.loading = "false";
          return;
        }

        let nextDay = "";
        let deliveryMessageContent = "";

        const myHeaders = new Headers();
        myHeaders.append("Content-Type", "application/json");

        const raw = JSON.stringify({
          rate: {
            destination: {
              country: "UK",
              postal_code: "MK45 5BX ",
              city: "Flitwick",
              address1: "11 Enterprise Way",
              phone: "01525722722",
              company_name: "Mookie Toys",
            },
            items: [
              {
                product_id: productId,
                requires_shipping: true,
                quantity: 1,
                price: productPrice,
                sku: productSku,
              },
            ],
            currency: "GBP",
            locale: "en",
          },
        });

        const requestOptions = {
          method: "POST",
          headers: myHeaders,
          body: raw,
          redirect: "follow",
        };

        fetch(
          "https://tptoys.qfconnector.com/api/carrier-service",
          requestOptions,
        )
          .then(async (response) => {
            const responseText = await response.text();

            if (!response.ok) {
              throw new Error(
                `Carrier service error: ${response.status} ${response.statusText}`,
              );
            }

            return JSON.parse(responseText);
          })
          .then((result) => {
            const rates = result?.rates || [];
            let earliestServiceName = "";

            if (rates.length > 0) {
              let earliestDate = null;

              rates.forEach((availableDate) => {
                const dateParts = availableDate.service_code
                  .split("|")[1]
                  .split("-");
                const date = new Date(
                  dateParts[0],
                  dateParts[1] - 1,
                  dateParts[2],
                );

                if (
                  earliestDate === null ||
                  date.getTime() < earliestDate.getTime()
                ) {
                  earliestDate = date;
                  const dayDate = new Intl.DateTimeFormat("en-US", {
                    day: "numeric",
                  }).format(date);
                  const dayDatePrefix =
                    dayDate > 0
                      ? ["th", "st", "nd", "rd"][
                          (dayDate > 3 && dayDate < 21) || dayDate % 10 > 3
                            ? 0
                            : dayDate % 10
                        ]
                      : "";
                  nextDay = `${new Intl.DateTimeFormat("en-US", {
                    weekday: "long",
                  }).format(date)}  ${
                    dayDate + dayDatePrefix
                  }  ${new Intl.DateTimeFormat("en-US", {
                    month: "long",
                  }).format(date)}`;
                  earliestServiceName = availableDate.description;
                }
              });

              deliveryMessageContent = earliestServiceName;
            } else if (rates[0]) {
              deliveryMessageContent = rates[0].service_name;
            }
          })
          .catch((error) => console.log("error", error))
          .then(() => {
            if (!nextDay) {
              titleEl.innerText = deliveryMessageContent;
            } else {
              titleEl.innerText = `${
                deliveryMessage.dataset.quickestDeliveryMessage || ""
              } ${nextDay}`.trim();
            }
          })
          .finally(() => {
            deliveryMessage.dataset.loading = "false";
          });
      });
    }

    initProductForms() {
      let productForms = document.querySelectorAll('form[action="/cart/add"]');

      const stickyButtons = document.querySelectorAll(
        ".fixed-product-button.builder-template",
      );

      stickyButtons.forEach((stickyButton) => {
        if (stickyButton.dataset.stickyListenerAttached === "true") return;
        stickyButton.dataset.stickyListenerAttached = "true";

        stickyButton.addEventListener(
          "click",
          (e) => {
            const form = stickyButton.closest('form[action="/cart/add"]');
            if (!form) return;

            const regularButton = form.querySelector(
              'button[type="submit"]:not(.builder-template), input[type="submit"]:not(.builder-template)',
            );
            if (regularButton) {
              e.preventDefault();
              e.stopPropagation();

              regularButton.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
            }
          },
          true,
        );
      });

      productForms.forEach((productForm) => {
        productForm.addEventListener(
          "submit",
          function (e) {
            e.preventDefault();
            e.stopImmediatePropagation();

            const clickedButton =
              e.submitter ||
              (document.activeElement &&
              document.activeElement.classList.contains("builder-template")
                ? document.activeElement
                : null);

            if (
              clickedButton &&
              clickedButton.classList.contains("builder-template")
            ) {
              const regularButton = productForm.querySelector(
                'button[type="submit"]:not(.builder-template), input[type="submit"]:not(.builder-template)',
              );

              if (regularButton) {
                regularButton.scrollIntoView({
                  behavior: "smooth",
                  block: "center",
                });
                return;
              }
            }

            let submitBtn = productForm.querySelector(
              'button[type="submit"], input[type="submit"]',
            );
            let quantity = productForm.querySelector(
              "input.quantity-selector",
            ).value;
            let id = productForm.querySelector("#product-id").value;
            let groupId = null;

            // Check for accessories
            const accessoryWrapper = document.querySelector(
              ".tp-product-accessories",
            );
            const accessoryInputs = accessoryWrapper
              ? accessoryWrapper.querySelectorAll(".tp-product-accessory-input")
              : [];

            let anyAccessoryInputsChecked = false;

            accessoryInputs.forEach((input) => {
              if (input.checked) {
                anyAccessoryInputsChecked = true;
              }
            });

            if (anyAccessoryInputsChecked) {
              groupId = crypto.randomUUID();
            }

            let items = [];
            if (quantity && id) {
              const baseItem = {
                id: id,
                quantity: quantity,
              };

              if (groupId) {
                baseItem.properties = {
                  "Group ID": groupId,
                  "Group Type": "Parent",
                };
              }

              items.push(baseItem);
            }

            if (!groupId) {
              if (items.length > 0 && submitBtn) {
                const addingToCartText =
                  window.cartStrings?.addingToCart || "Adding to cart";
                const addedToCartText =
                  window.themeStrings?.addedToCart ||
                  window.cartStrings?.addToCart ||
                  "Added to cart";
                window.cart.add({ items: items }).then(() => {
                  if (submitBtn.tagName === "INPUT")
                    submitBtn.value = addedToCartText;
                  else {
                    const el = submitBtn.querySelector(".add-to-cart-label");
                    if (el) el.textContent = addedToCartText;
                  }
                });
              }
              return;
            }

            accessoryInputs.forEach((input) => {
              if (input.checked) {
                const productId = input.dataset.productId;
                items.push({
                  id: productId,
                  quantity: 1,
                  properties: {
                    "Group ID": groupId,
                    "Group Type": "Add-on",
                  },
                });
              }
            });

            if (items.length > 0 && submitBtn) {
              const addingToCartText =
                window.cartStrings?.addingToCart || "Adding to cart";
              const addedToCartText =
                window.themeStrings?.addedToCart ||
                window.cartStrings?.addToCart ||
                "Added to cart";
              if (submitBtn.tagName === "INPUT")
                submitBtn.value = addingToCartText;
              else {
                const el = submitBtn.querySelector(".add-to-cart-label");
                if (el) el.textContent = addingToCartText;
              }
              window.cart.add({ items: items }).then(() => {
                if (submitBtn.tagName === "INPUT")
                  submitBtn.value = addedToCartText;
                else {
                  const el = submitBtn.querySelector(".add-to-cart-label");
                  if (el) el.textContent = addedToCartText;
                }
              });
            }
          },
          true,
        );
      });
    }

    initAccessoryInputClickListeners() {
      const accessoryWrapper = document.querySelector(
        ".tp-product-accessories",
      );
      if (!accessoryWrapper) return;
      const accessoryInputs = accessoryWrapper.querySelectorAll(
        ".tp-product-accessory-input",
      );

      accessoryInputs.forEach((input) => {
        input.addEventListener("change", () =>
          this.handleAccessoryInputClick(input),
        );
      });
    }

    handleAccessoryInputClick(input) {
      const parentWrapper = input.closest(".tp-product-accessories");
      if (!parentWrapper) return;

      const accessoryLimit = parentWrapper.dataset.accessoryLimit;
      const accessoryInputs = parentWrapper.querySelectorAll(
        ".tp-product-accessory-input",
      );

      const accessoryWrapperHeader = parentWrapper.parentElement.querySelector(
        ".tp-product-features-container-header",
      );
      const accessoryLimitText = accessoryWrapperHeader.querySelector(
        "#tp-product-accessory-limit",
      );

      let totalAccessoryPrice = 0;
      let totalAccessoryCount = 0;

      accessoryInputs.forEach((input) => {
        if (input.checked) {
          totalAccessoryPrice += parseFloat(input.dataset.price);
          totalAccessoryCount++;
        }
      });

      if (totalAccessoryCount >= accessoryLimit) {
        parentWrapper.setAttribute("data-accessory-limit-reached", "true");
        accessoryLimitText.innerText =
          accessoryLimitText.dataset.limitText ?? "";
      } else if (totalAccessoryCount < accessoryLimit) {
        parentWrapper.setAttribute("data-accessory-limit-reached", "false");
        const remainingAccessoryCount = accessoryLimit - totalAccessoryCount;

        if (remainingAccessoryCount === 1) {
          accessoryLimitText.innerText =
            accessoryLimitText.dataset.singleText.replace(
              "{{ count }}",
              remainingAccessoryCount,
            );
        } else {
          accessoryLimitText.innerText =
            accessoryLimitText.dataset.pluralText.replace(
              "{{ count }}",
              remainingAccessoryCount,
            );
        }
      }

      parentWrapper.setAttribute(
        "data-total-accessory-price",
        totalAccessoryPrice,
      );

      const productForm = document.querySelector('form[action="/cart/add"]');
      const submitWrapper = productForm.querySelector(
        ".product-submit-wrapper",
      );
      const submitBtns = submitWrapper.querySelectorAll(
        'button[type="submit"], input[type="submit"]',
      );

      submitBtns.forEach((submitBtn) => {
        const formatted = window.Shopify.formatMoney(
          parseFloat(submitBtn.dataset.productPrice) +
            parseFloat(totalAccessoryPrice),
          window.theme.moneyFormat,
        );
        if (submitBtn.tagName === "INPUT")
          submitBtn.value = `Add to cart - ${formatted}`;
        else {
          const priceEl = submitBtn.querySelector(".submit-price");
          if (priceEl) priceEl.textContent = formatted;
        }
      });
    }

    initPopstateHandler() {
      if (this._onPopstate) return;
      this._onPopstate = async (event) => {
        if (event.state == null) return;

        const currentUrl = window.location.href;
        const url = new URL(currentUrl);
        const pathParts = url.pathname.split("/").filter((part) => part);

        if (pathParts.length >= 2 && pathParts[0] === "products") {
          const productHandle = pathParts[1];
          if (productHandle) {
            await this.restoreProduct(productHandle, currentUrl);
          }
        }
      };
      window.addEventListener("popstate", this._onPopstate);
    }

    removePopstateHandler() {
      if (!this._onPopstate) return;
      window.removeEventListener("popstate", this._onPopstate);
      this._onPopstate = null;
    }

    async restoreProduct(productHandle, targetUrl) {
      try {
        const sections = this.sectionsToReplace.split(",");

        const sectionsIds = sections.map((section) => {
          const sectionElement = document.querySelector(`.${section}`);
          return sectionElement
            ? sectionElement.id.replace("shopify-section-", "")
            : null;
        });

        const sectionsIdsString = sectionsIds.join(",");

        const response = await fetch(
          window.Shopify.routes.root +
            `products/${productHandle}?sections=${sectionsIdsString}`,
        );
        const sectionJson = await response.json();

        sectionsIds.forEach((sectionId) => {
          const currentContainer = document.querySelector(
            `#shopify-section-${sectionId}`,
          );
          if (!currentContainer) return;

          const newHtml = sectionJson[sectionId];
          currentContainer.innerHTML = newHtml;
        });

        this.initRecommendationsInReplacedSections(sectionsIds);

        if (window.innerWidth > 1023.98) {
          window.scrollTo({
            top: 0,
            behavior: "smooth",
          });
        }

        this.destroyGallerySwipers();
        this.removeResizeHandler();
        window.yotpo.initWidgets();

        this.bindFeatureInputs();
        this.initGallerySwipers();
        this.initDeliveryMessages();
        this.initProductForms();
        this.initAccessoryInputClickListeners();
      } catch (error) {
        console.error("Error restoring product:", error);
      }
    }
  }

  customElements.define("tp-product-features", TpProductFeatures);
}
